import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Check from '@material-ui/icons/Check';
import { makeStyles } from '@material-ui/styles';
import { Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';

import TextControl from 'src/components/forms/controls/text';
import Field from 'src/components/forms/field';
import InviteViaCode from 'src/components/pages/pageElements/videoConference/InviteViaCode';
import { REQUEST_A_PEER } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { EMAIL_INVITE_VALIDATOR } from 'src/shared/client/forms/validation/videoInvite';
import logger from 'src/shared/util/logger';
import { colors } from 'src/util/colors';

interface InviteGuestModalProps {
  eventId: string;
  guestUrl: string;
  inviteGuestByEmail: (email: string) => Promise<void>;
  invitePeerWithReason: (reason: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Used to signify the tab hasn't been changed, and we should have the default tab selected
 *
 * The actual default relies on the feature flags, so we need to use this temporary value
 * until we can determine the actual default tab.
 */
const DEFAULT_TAB = 'default';

export const InviteGuestModal: React.FC<InviteGuestModalProps> = ({
  eventId,
  guestUrl,
  inviteGuestByEmail,
  invitePeerWithReason,
  onCancel,
}) => {
  const classes = useStyles();
  const isRequestAPeerEnabled = useFeatureFlag(REQUEST_A_PEER);

  const [tabState, setTabState] = useState(DEFAULT_TAB);
  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChangeTab = (_evt: React.SyntheticEvent, value: string) => {
    setTabState(value);
  };

  const copyVideoURL = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (err) {
      logger.error('Failed to copy: ', err);
    }
  };

  const sendEmailInvite = async (
    values: { email: string },
    actions: FormikHelpers<{ email: string }>,
  ) => {
    try {
      await inviteGuestByEmail(values.email);
      actions.setSubmitting(false);
      setSubmitted(true);
    } catch {
      actions.setSubmitting(false);
      actions.setFieldError(
        'email',
        'An unexpected error occurred and the email could not be sent. \nPlease close the modal and try again.',
      );
    }
  };

  const sendPeerInvite = async (
    values: { peerReason: string },
    actions: FormikHelpers<{ peerReason: string }>,
  ) => {
    try {
      await invitePeerWithReason(values.peerReason);
      actions.setSubmitting(false);
      setSubmitted(true);
    } catch {
      actions.setSubmitting(false);
      actions.setFieldError(
        'peerReason',
        'An unexpected error occurred and the peer invite request could not be sent. \nPlease close the modal and try again.',
      );
    }
  };

  // eslint-disable-next-line no-nested-ternary
  const tab = tabState === DEFAULT_TAB ? (isRequestAPeerEnabled ? 'peer' : 'code') : tabState;

  return (
    <Dialog open fullWidth onClose={onCancel}>
      <Paper elevation={2} variant="outlined">
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          centered
          TabIndicatorProps={{
            style: {
              backgroundColor: 'white',
            },
          }}
        >
          {isRequestAPeerEnabled && <Tab label="Peer" value="peer" />}
          <Tab label="Code" value="code" />
          <Tab label="Email" value="email" />
          <Tab label="URL" value="url" />
        </Tabs>
      </Paper>
      <DialogTitle>Invite a guest to join this visit</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {tab === 'email' && (
          <Formik
            initialValues={{ email: '' }}
            onSubmit={sendEmailInvite}
            validationSchema={EMAIL_INVITE_VALIDATOR}
          >
            {({ handleSubmit, isSubmitting, isValid, dirty }) => (
              <form className={classes.twoColumn} onSubmit={handleSubmit}>
                <Field name="email" component={TextControl} label="Email" autoFocus />
                <Button
                  className={classes.rightButton}
                  color="primary"
                  size="medium"
                  variant="contained"
                  disabled={!isValid || !dirty || isSubmitting || submitted}
                  type="submit"
                >
                  {isSubmitting && <CircularProgress />}
                  {submitted && <Check />}
                  {!isSubmitting && <span>{submitted ? 'Sent' : 'Email Link'}</span>}
                </Button>
              </form>
            )}
          </Formik>
        )}
        {tab === 'url' && (
          <div className={classes.twoColumn}>
            <div>
              <span className={classes.urlLabel}>url</span>
              <p className={classes.urlText}>{guestUrl}</p>
            </div>
            <Button
              className={classes.rightButton}
              size="medium"
              color="primary"
              variant="contained"
              onClick={() => copyVideoURL(guestUrl)}
              disabled={copied}
            >
              {copied && <Check />}
              {copied ? 'Copied' : 'Copy Link'}
            </Button>
          </div>
        )}
        {tab === 'code' && <InviteViaCode eventId={eventId} onJoin={onCancel} />}
        {isRequestAPeerEnabled && tab === 'peer' && (
          <Formik initialValues={{ peerReason: '' }} onSubmit={sendPeerInvite}>
            {({ handleSubmit, isSubmitting }) => (
              <form className={classes.twoColumn} onSubmit={handleSubmit}>
                <Field
                  name="peerReason"
                  component={TextControl}
                  label="Reason (optional)"
                  autoFocus
                />
                <Button
                  className={classes.rightButton}
                  color="primary"
                  size="medium"
                  variant="contained"
                  disabled={isSubmitting || submitted}
                  type="submit"
                >
                  {isSubmitting && <CircularProgress />}
                  {submitted && <Check />}
                  {!isSubmitting && <span>{submitted ? 'Sent' : 'Invite'}</span>}
                </Button>
              </form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles({
  dialogContent: {
    padding: '0 24px 24px', // Keep old material UI settings
  },
  urlLabel: {
    color: 'grey',
    textTransform: 'uppercase',
  },
  urlText: {
    wordBreak: 'break-word',
  },
  tabs: {
    backgroundColor: colors.primary,
    color: 'white',
  },
  twoColumn: {
    display: 'flex',
  },
  rightButton: {
    height: '100%',
    marginLeft: 30,
    minWidth: 120,
  },
});
